'use client';

import { useEffect, useRef, useState } from 'react';
import { Github, ExternalLink } from 'lucide-react';
import { PROJECTS } from '@/data/portfolioData';
import SectionHeader from './SectionHeader';

const STATUS_CONFIG = {
  completed: { label: 'Completed', color: '#7d9b8a' },
  in_progress: { label: 'In Progress', color: '#c9a87c' },
  not_complete: { label: 'Not Completed', color: '#c47d6a' },
};

export default function ProjectsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [expandedProjects, setExpandedProjects] = useState<Record<number, boolean>>({});

  const toggleExpand = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedProjects((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.05 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="projects" ref={sectionRef} className="py-28 xl:py-40 relative">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
        <div
          className={`transition-all duration-800 ease-organic ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <SectionHeader index="04 — Portfolio" title="Featured" highlight="projects" />
        </div>

        {/* Asymmetric staggered layout */}
        <div className="space-y-8 xl:space-y-12">
          {PROJECTS.map((project, i) => {
            const statusCfg = STATUS_CONFIG[project.status as keyof typeof STATUS_CONFIG];
            const isHovered = hoveredId === project.id;
            const isExpanded = !!expandedProjects[project.id];
            const isForsa = project.title.toLowerCase() === 'forsa' || !!project.liveUrl;
            const isFeatured = i === 0;

            return (
              <article
                key={project.id}
                className={`group warm-card rounded-2xl overflow-hidden transition-all duration-600 ease-organic ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                } ${isFeatured ? 'xl:mr-24' : i % 2 === 1 ? 'xl:ml-16' : ''}`}
                style={{
                  transitionDelay: `${i * 100}ms`,
                  borderColor: isHovered ? `${project.color}35` : undefined,
                  transform: isHovered ? 'translateY(-3px)' : undefined,
                }}
                onMouseEnter={() => setHoveredId(project.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <div className={`grid grid-cols-1 ${isFeatured ? 'lg:grid-cols-5' : 'lg:grid-cols-3'} gap-0`}>
                  {/* Image panel */}
                  <div
                    className={`relative overflow-hidden ${isFeatured ? 'lg:col-span-2' : 'lg:col-span-1'} min-h-[200px] lg:min-h-[240px]`}
                    style={{
                      backgroundImage: project.backgroundImage
                        ? `url(${project.backgroundImage})`
                        : `linear-gradient(135deg, ${project.color}25, ${project.color}08)`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  >
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${project.gradient} transition-all duration-500 ${
                        isForsa ? 'group-hover:backdrop-blur-sm group-hover:bg-background/50' : ''
                      }`}
                    />

                    {isForsa && (
                      <div className="absolute inset-0 flex items-center justify-center z-10 opacity-0 group-hover:opacity-100 transition-all duration-400">
                        <a
                          href={project.liveUrl || 'https://forsa-app.runasp.net/'}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="btn-primary shadow-warm"
                        >
                          Visit Forsa
                          <ExternalLink size={14} />
                        </a>
                      </div>
                    )}

                    <div className="absolute top-4 right-4 z-10">
                      <span
                        className="px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider border backdrop-blur-sm"
                        style={{
                          color: statusCfg.color,
                          borderColor: `${statusCfg.color}35`,
                          backgroundColor: `${statusCfg.color}12`,
                        }}
                      >
                        {statusCfg.label}
                      </span>
                    </div>

                    <div className="absolute bottom-4 left-4 z-10">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-parchment/70 bg-background/50 backdrop-blur-sm px-2 py-1 rounded border border-parchment/10">
                        {project.category}
                      </span>
                    </div>
                  </div>

                  {/* Content panel */}
                  <div className={`p-6 xl:p-8 ${isFeatured ? 'lg:col-span-3' : 'lg:col-span-2'}`}>
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <h3 className="font-display text-xl xl:text-2xl text-parchment font-700">
                        {project.title}
                      </h3>
                      {project.liveUrl && (
                        <a
                          href={project.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link-hover text-sand text-xs font-mono flex items-center gap-1 shrink-0"
                        >
                          Live <ExternalLink size={12} />
                        </a>
                      )}
                    </div>

                    <div className="mb-5">
                      <p
                        className={`text-parchment-muted leading-relaxed ${
                          isExpanded ? '' : 'line-clamp-2'
                        }`}
                      >
                        {project.description}
                      </p>
                      <button
                        type="button"
                        onClick={(e) => toggleExpand(project.id, e)}
                        className="mt-2 text-xs font-mono text-sand/70 hover:text-sand transition-colors duration-200"
                      >
                        {isExpanded ? '— less' : '— more'}
                      </button>
                    </div>

                    {project.sections && project.sections.length > 0 && isExpanded && (
                      <div className="mb-5 pt-4 border-t border-parchment/8 space-y-2">
                        {project.sections.map((sec, idx) => (
                          <p key={idx} className="text-parchment-muted text-sm leading-relaxed">
                            <span className="text-sand font-600">{sec.title}:</span> {sec.content}
                          </p>
                        ))}
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 mb-6">
                      {project.tech.map((t) => (
                        <span
                          key={t}
                          className="px-2.5 py-1 rounded-md text-[11px] font-mono border"
                          style={{
                            color: project.color,
                            borderColor: `${project.color}25`,
                            backgroundColor: `${project.color}08`,
                          }}
                        >
                          {t}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center justify-between pt-5 border-t border-parchment/6">
                      <a
                        href={project.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link-hover text-parchment-muted hover:text-parchment text-xs font-mono flex items-center gap-1.5 transition-colors duration-300"
                      >
                        <Github size={14} />
                        GitHub
                      </a>
                      {project.liveUrl && (
                        <a
                          href={project.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link-hover text-sand text-xs font-mono flex items-center gap-1.5"
                        >
                          <ExternalLink size={14} />
                          Visit
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
